import { motion } from "framer-motion";
import { ArrowUpRight, ConciergeBell, LineChart, Megaphone, Compass, HeartHandshake } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { useSite } from "../context/SiteContext";
import { img } from "../lib/images";

const ICONS = [ConciergeBell, LineChart, Megaphone, Compass];

const ServiceCard = ({ s, i }) => {
  const Icon = ICONS[i % ICONS.length];
  return (
    <Reveal delay={i * 0.08} className="h-full">
      <motion.article whileHover={{ y: -6 }} transition={{ type: "spring", stiffness: 300, damping: 24 }} className="group flex h-full flex-col rounded-[24px] border border-ink/10 bg-white p-7 sm:p-8" data-testid={`service-card-${i}`}>
        <div className="flex items-start justify-between">
          <span className="grid h-12 w-12 place-items-center rounded-2xl bg-mint text-ink transition-colors duration-300 group-hover:bg-ink group-hover:text-sand">
            <Icon className="h-5 w-5" />
          </span>
          <span className="font-display text-lg text-ink/30">0{i + 1}</span>
        </div>
        <h3 className="mt-10 font-display text-2xl text-ink">{s.title}</h3>
        <p className="mt-3 text-[15px] leading-relaxed text-ink/70">{s.text}</p>
        <div className="mt-auto flex flex-wrap gap-2 pt-7">
          {(s.tags || []).map((t) => (
            <span key={t} className="rounded-full bg-sand px-3 py-1 text-xs font-medium text-ink/70">{t}</span>
          ))}
        </div>
      </motion.article>
    </Reveal>
  );
};

const CommunityCard = ({ c }) => (
  <Reveal delay={0.1} className="h-full md:col-span-2">
    <article className="relative flex h-full min-h-[340px] overflow-hidden rounded-[24px] bg-ink" data-testid="community-card">
      <img {...img("aerial", "(min-width: 768px) 66vw, 100vw")} loading="lazy" decoding="async" alt="Aerial view of Bali rice fields" className="absolute inset-0 h-full w-full object-cover opacity-45" />
      <div className="relative mt-auto max-w-lg p-7 sm:p-10">
        <span className="inline-flex items-center gap-2 rounded-full bg-sand/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-sand backdrop-blur">
          <HeartHandshake className="h-3.5 w-3.5" /> Community Contribution
        </span>
        <h3 className="mt-5 font-display text-3xl text-sand">{c.title}</h3>
        <p className="mt-3 leading-relaxed text-sand/85">{c.text}</p>
        <a href="#journey" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-clay-soft" data-testid="community-purpose-link">
          Our purpose <ArrowUpRight className="h-4 w-4" />
        </a>
      </div>
    </article>
  </Reveal>
);

export const Services = () => {
  const site = useSite();
  return (
  <section id="services" data-testid="services-section" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
    <div className="grid gap-6 lg:grid-cols-12 lg:items-end">
      <Reveal className="lg:col-span-7">
        <Eyebrow>What we do</Eyebrow>
        <h2 className="font-display text-4xl leading-[1.08] text-ink sm:text-5xl">{site.services_intro.title}</h2>
      </Reveal>
      <Reveal delay={0.1} className="lg:col-span-5">
        <p className="text-base leading-relaxed text-ink/70 sm:text-lg">{site.services_intro.text}</p>
      </Reveal>
    </div>
    <div className="mt-14 grid auto-rows-fr gap-5 md:grid-cols-2 lg:grid-cols-4">
      {site.services.map((s, i) => <ServiceCard key={i} s={s} i={i} />)}
    </div>
    <div className="mt-5 grid gap-5 md:grid-cols-3">
      <CommunityCard c={site.community} />
      <Reveal delay={0.15} className="h-full">
        <div className="flex h-full flex-col justify-between rounded-[24px] bg-clay p-7 text-sand sm:p-8" data-testid="channels-card">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sand/80">Multi-channel distribution</p>
          <div className="mt-8 space-y-2 font-display text-2xl">
            {["Booking.com", "Agoda", "Expedia", "Airbnb"].map((c) => <p key={c}>{c}</p>)}
          </div>
          <p className="mt-8 text-sm text-sand/90">Dynamic pricing and targeted digital marketing across every major OTA.</p>
        </div>
      </Reveal>
    </div>
  </section>
  );
};
